import { TFilterValues } from '@components/table/types/TableFilterValues.type';
import { TSortValue } from '@components/table/types/SortValue.type';
import { createContext } from 'react';
import { createStore } from 'zustand';
import { persist } from 'zustand/middleware';

export type TableStore = {
  hiddenColumns: { [columnName: string]: boolean };
  queryParams?: { sortValue?: TSortValue; filters?: TFilterValues };
  updateHiddenColumns: (k: string, v: boolean) => void;
  updateQueryParams: (queryParams?: any) => void;
  updateFiltersLoaded: (loaded?: boolean) => void;
  updateShowFilters: (showFilters?: boolean) => void;
  /** Track for original load state such as defaulting filters to open should only happen when not loaded */
  filtersLoaded?: boolean;
  showFilters?: boolean;

  // filters applied to the table
  appliedFilters?: TFilterValues;
  updateAppliedFilters: (filters: TFilterValues) => void;

  /** If pinned filters are turned on */
  pinnedFiltersEnabled: boolean;
  /** All pinned filters and if they are currently enabled or not */
  pinnedFilters: TFilterValues;
  togglePinnedFilters: (enabled: boolean) => void;
  /** Used to add a pinned filter - will add as not enabled */
  addPinnedFilter: ({ field, value }: { field: string; value: any }) => void;
  /** Used to remove a single pinned filter */
  removePinnedFilter: (field: string) => void;
};

export const initialState = {
  hiddenColumns: {},
  filtersLoaded: false,
  showFilters: false,
  appliedFilters: {},
  updateAppliedFilters: undefined,
  pinnedFiltersEnabled: false,
  pinnedFilters: {},
  togglePinnedFilters: undefined,
  addPinnedFilter: undefined,
  removePinnedFilter: undefined,
};

// TODO/FUTURE: default column visibility for 'initial' store (not necessary for the current use case)
export const createTableStore = (initProps: { tableId: string; hiddenColumns?: { [columnName: string]: boolean } }) => {
  return createStore<TableStore>()(
    persist(
      (set) => ({
        ...initialState,
        ...initProps,
        updateHiddenColumns: (k, v) =>
          set((state) => {
            if (v) {
              return { hiddenColumns: { ...state.hiddenColumns, [k]: v } };
            }

            const newColumns = { ...state.hiddenColumns };
            delete newColumns[k];
            return { hiddenColumns: newColumns };
          }),
        updateQueryParams: (queryParams) =>
          set(() => {
            return { queryParams };
          }),
        updateFiltersLoaded: (filtersLoaded) =>
          set(() => {
            return { filtersLoaded };
          }),
        updateShowFilters: (showFilters) =>
          set(() => {
            return { showFilters };
          }),
        updateAppliedFilters: (appliedFilters) =>
          set(() => {
            return { appliedFilters };
          }),
        addPinnedFilter: ({ field, value }) =>
          set((state) => {
            const newPinnedFilters = {
              ...state.pinnedFilters,
              [field]: value,
            };
            return { pinnedFilters: newPinnedFilters };
          }),
        removePinnedFilter: (field) =>
          set((state) => {
            const { [field]: _, ...remainingPinnedFilters } = state.pinnedFilters || {};
            return { pinnedFilters: remainingPinnedFilters };
          }),
        togglePinnedFilters: (enabled) =>
          set(() => {
            return { pinnedFiltersEnabled: enabled };
          }),
      }),
      {
        name: `table-store-${initProps.tableId}`, // unique name for the storage key
        version: 1.2,
        partialize: (state): TableStore => {
          return {
            ...state,
            filtersLoaded: false, // force to be false on load
          } as TableStore;
        },
      },
    ),
  );
};

export type TableStoreApi = ReturnType<typeof createTableStore>;

export const TableStoreContext = createContext<TableStoreApi | undefined>(undefined);
